import React from "react";

import { Controller, useFormContext } from "react-hook-form";

import { useFlows } from "src/api/queries";
import { ErrorHint } from "src/base-components/ErrorHint";
import { Label } from "src/base-components/Label";
import { InvalidFlowDisplay } from "src/cases/templates/common/InvalidFlowDisplay";
import { FormStepWrapper } from "src/cases/templates/FormStepWrapper";
import { getStepById } from "src/cases/templates/formSteps";
import { CaseTemplateFormValues } from "src/cases/templates/formUtils/types";
import { useCheckFieldPermission } from "src/cases/templates/useCheckFieldPermission";
import { Tooltip } from "src/design-system/Tooltip";
import { FlowAndVersionDropdown } from "src/flowsOverview/FlowAndVersionDropdown";
import { useWorkspaceContext } from "src/router/routerContextHooks";

export const SelectDecisionFlowStep: React.FC = () => {
  const { workspace } = useWorkspaceContext();
  const step = getStepById("select-decision-flow");
  const form = useFormContext<CaseTemplateFormValues>();
  const hasPermission = useCheckFieldPermission();

  const {
    formState: { errors },
  } = form;

  const flowsQuery = useFlows({
    workspaceId: workspace.id,
    includedTypes: ["case_flow"],
  });

  return (
    <FormStepWrapper
      description={step.description}
      docLink={step.docLink}
      title={step.title}
    >
      <div className="flex flex-col content-stretch items-start justify-start gap-3 rounded-lg bg-gray-50 px-4 py-3">
        <div className="flex w-full items-start justify-start gap-2">
          <div className="flex flex-1 flex-col">
            <Label>Case preparation flow</Label>
            <Controller
              name="attributes.case_creation_flow_details"
              render={({ field }) => {
                const isLoading =
                  !!field.value?.flow_slug && !flowsQuery.isFullyLoaded;

                const isInvalidFlow =
                  !!field.value?.flow_slug &&
                  flowsQuery.isFullyLoaded &&
                  !flowsQuery.isError &&
                  !flowsQuery.data?.some(
                    (f) => f.slug === field.value?.flow_slug,
                  );

                if ((isInvalidFlow || isLoading) && field.value) {
                  return (
                    <InvalidFlowDisplay
                      dataLoc="case-preparation-flow"
                      flowSlug={field.value.flow_slug}
                      loading={isLoading}
                      onClear={() => field.onChange(null)}
                    />
                  );
                }

                if (flowsQuery.isError) {
                  return (
                    <ErrorHint>
                      Failed to load Decision Flows. Please try again.
                    </ErrorHint>
                  );
                }
                const flows = flowsQuery.data ?? [];
                const currentFlow = flows.find(
                  (flow) => flow.slug === field.value?.flow_slug,
                );
                const currentVersion = currentFlow?.versions?.find(
                  (version) => version.name === field.value?.version_name,
                );

                const selectedValue =
                  currentFlow && currentVersion
                    ? {
                        id: currentFlow.id,
                        versionId: currentVersion.id,
                      }
                    : null;

                const dropdown = (
                  <FlowAndVersionDropdown
                    disabled={
                      !flows.length ||
                      flowsQuery.isLoading ||
                      !hasPermission(
                        "edit",
                        "attributes.case_creation_flow_details",
                      )
                    }
                    flows={flows}
                    placeholder="Select Case preparation flow and version"
                    selected={selectedValue}
                    clearable
                    onSelect={(value) => {
                      if (!value) {
                        field.onChange(null);
                      } else {
                        if (value.flowSlug && value.versionName) {
                          field.onChange({
                            flow_slug: value.flowSlug,
                            version_name: value.versionName,
                          });
                        }
                      }
                    }}
                  />
                );

                return (
                  <>
                    <div>
                      {flows.length === 0 ? (
                        <Tooltip
                          body="There is no Decision Flow in your workspace that takes a case as input"
                          placement="top"
                          asChild
                        >
                          <div>{dropdown}</div>
                        </Tooltip>
                      ) : (
                        <div className="w-full">{dropdown}</div>
                      )}
                    </div>
                    {errors.attributes?.case_creation_flow_details?.flow_slug
                      ?.message && (
                      <ErrorHint>
                        {
                          errors.attributes.case_creation_flow_details.flow_slug
                            .message
                        }
                      </ErrorHint>
                    )}
                  </>
                );
              }}
            />
          </div>
        </div>
      </div>
    </FormStepWrapper>
  );
};
